/**
 * Implementación basada en Web Serial API.
 * Permite imprimir directamente desde el navegador (sin agente) cuando el
 * cliente otorga permisos, generando comandos ESC/POS compatibles con la
 * Hasar P-HAS-181. Hoy se usa como fallback opcional.
 */
import ESC from 'escpos-buffer';

// Configuración de la impresora Hasar P-HAS-181
const HASAR_CONFIG = {
  vendorId: 0x0471,
  productId: 0x0055,
  name: 'Hasar P-HAS-181'
};

// Configuración de impresión
const PRINTER_CONFIG = {
  width: 42, // Caracteres por línea para papel de 80mm
  encoding: 'cp437' // Encoding compatible con impresoras térmicas
};

/**
 * Clase para manejar la impresión térmica directa desde el navegador
 */
class ThermalPrinter {
  constructor() {
    this.device = null;
    this.port = null;
    this.isConnected = false;
  }

  /**
   * Detecta y conecta con la impresora Hasar P-HAS-181
   * @returns {Promise<boolean>} true si se conecta exitosamente
   */
  async connect() {
    try {
      console.log('🔍 Buscando impresora Hasar P-HAS-181...');
      
      // Verificar si Web Serial API está disponible
      if (!('serial' in navigator)) {
        throw new Error('Web Serial API no está disponible en este navegador. Use Chrome o Edge.');
      }

      // Solicitar acceso al puerto serie sin filtros específicos
      // Esto permitirá al usuario seleccionar manualmente la impresora
      const port = await navigator.serial.requestPort();

      console.log('📱 Puerto seleccionado:', port);

      // Abrir conexión con configuración estándar para impresoras térmicas
      await port.open({ 
        baudRate: 9600,
        dataBits: 8,
        stopBits: 1,
        parity: 'none',
        flowControl: 'none'
      });

      this.port = port;
      this.isConnected = true;
      
      console.log('✅ Conectado a impresora');
      return true;

    } catch (error) {
      console.error('❌ Error conectando con impresora:', error);
      this.isConnected = false;
      
      // Proporcionar mensajes de error más específicos
      if (error.name === 'SecurityError') {
        throw new Error('Permisos denegados. Asegúrese de seleccionar la impresora correcta.');
      } else if (error.name === 'NotFoundError') {
        throw new Error('No se encontró la impresora. Verifique que esté conectada y encendida.');
      } else {
        throw new Error(`Error de conexión: ${error.message}`);
      }
    }
  }

  /**
   * Desconecta de la impresora
   */
  async disconnect() {
    try {
      if (this.port && this.isConnected) {
        await this.port.close();
        this.port = null;
        this.isConnected = false;
        console.log('🔌 Desconectado de la impresora');
      }
    } catch (error) {
      console.error('❌ Error desconectando:', error);
    }
  }

  /**
   * Envía comandos ESC/POS a la impresora
   * @param {Buffer} buffer - Buffer con comandos ESC/POS
   */
  async sendBuffer(buffer) {
    if (!this.isConnected || !this.port) {
      throw new Error('Impresora no conectada');
    }

    try {
      const writer = this.port.writable.getWriter();
      await writer.write(buffer);
      writer.releaseLock();
      console.log('📄 Comandos enviados a la impresora');
    } catch (error) {
      console.error('❌ Error enviando datos:', error);
      throw error;
    }
  }

  /**
   * Imprime un ticket con el contenido especificado
   * @param {Object} ticketData - Datos del ticket a imprimir
   */
  async printTicket(ticketData) {
    try {
      console.log('🖨️ Iniciando impresión de ticket...');

      // Crear buffer ESC/POS
      const buffer = this.createTicketBuffer(ticketData);
      
      // Enviar a la impresora
      await this.sendBuffer(buffer);
      
      console.log('✅ Ticket enviado a la impresora');
      return true;

    } catch (error) {
      console.error('❌ Error imprimiendo ticket:', error);
      throw error;
    }
  }

  /**
   * Crea el buffer ESC/POS para el ticket
   * @param {Object} ticketData - Datos del ticket
   * @returns {Buffer} Buffer con comandos ESC/POS
   */
  createTicketBuffer(ticketData) {
    const { business, sale, customer, footerMessage } = ticketData;
    
    // Crear buffer ESC/POS
    const buffer = new ESC();

    // Inicializar impresora
    buffer.init();

    // Encabezado del negocio (centrado y en negrita)
    buffer
      .align('center')
      .style('bold')
      .size(1, 1)
      .text(this.cleanText(business.name || 'Mi Negocio'))
      .style('normal')
      .size(0, 0);

    // Información del negocio
    if (business.address) {
      buffer.text(this.cleanText(business.address));
    }
    if (business.phone) {
      buffer.text(`Tel: ${this.cleanText(business.phone)}`);
    }
    if (business.cuit) {
      buffer.text(`C.U.I.T.: ${this.cleanText(business.cuit)}`);
    }
    if (business.condicionIva) {
      buffer.text(this.cleanText(business.condicionIva));
    }

    // Línea separadora
    buffer.text(this.generateLine());

    // Información del ticket
    buffer
      .align('left')
      .text(`Ticket Nro: ${sale.id || '-'}`)
      .text(`Fecha/Hora: ${this.formatDate(sale.dateISO)}`);

    // Información del cliente
    if (customer && (customer.name || customer.numeroDoc)) {
      buffer.text(this.generateLine());
      if (customer.name && customer.name !== 'Consumidor Final') {
        buffer.text(`Cliente: ${this.cleanText(customer.name)}`);
      } else {
        buffer.text('A CONSUMIDOR FINAL');
      }
      if (customer.numeroDoc && customer.numeroDoc !== '0') {
        buffer.text(`DNI/CUIT/CUIL: ${customer.numeroDoc}`);
      }
    } else {
      buffer.text(this.generateLine()).text('A CONSUMIDOR FINAL');
    }

    // Detalle de productos
    if (sale.items && sale.items.length > 0) {
      buffer.text(this.generateLine());
      buffer.text('DETALLE DE PRODUCTOS');
      buffer.text(this.generateLine());
      
      // Encabezado de columnas
      buffer.text(this.formatItemHeader());
      
      // Items
      sale.items.forEach(item => {
        const total = (item.precioUnitario || 0) * (item.cantidad || 0);
        buffer.text(this.formatItemLine({
          cantidad: item.cantidad,
          descripcion: item.descripcion,
          precioUnitario: item.precioUnitario,
          total: total
        }));
      });
      
      buffer.text(this.generateLine());
    }

    // Total
    buffer.text(`TOTAL: ${this.formatCurrency(sale.total || 0)}`);
    buffer.text(this.generateLine());

    // Mensaje de despedida (centrado)
    buffer
      .align('center')
      .text(footerMessage || 'Gracias por su compra');

    // Saltos de línea para facilitar el corte
    buffer.feed(6);

    // Cortar papel
    buffer.cut();

    return buffer.buffer();
  }

  /**
   * Limpia el texto para impresión térmica
   * @param {string} text - Texto a limpiar
   * @returns {string} Texto limpio
   */
  cleanText(text) {
    if (!text) return '';
    
    return String(text)
      // Reemplazar acentos
      .replace(/á/g, 'a').replace(/Á/g, 'A')
      .replace(/é/g, 'e').replace(/É/g, 'E')
      .replace(/í/g, 'i').replace(/Í/g, 'I')
      .replace(/ó/g, 'o').replace(/Ó/g, 'O')
      .replace(/ú/g, 'u').replace(/Ú/g, 'U')
      .replace(/ñ/g, 'n').replace(/Ñ/g, 'N')
      .replace(/ü/g, 'u').replace(/Ü/g, 'U')
      // Otros caracteres problemáticos
      .replace(/°/g, 'o')
      .replace(/¿/g, '?')
      .replace(/¡/g, '!')
      // Mantener caracteres básicos
      .replace(/[^\x20-\x7E]/g, '?');
  }

  /**
   * Formatea fecha para el ticket
   * @param {string} dateISO - Fecha en formato ISO
   * @returns {string} Fecha formateada
   */
  formatDate(dateISO) {
    if (!dateISO) return new Date().toLocaleString('es-AR');
    
    try {
      const date = new Date(dateISO);
      return date.toLocaleString('es-AR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
    } catch (error) {
      return new Date().toLocaleString('es-AR');
    }
  }

  /**
   * Genera una línea separadora
   * @returns {string} Línea de guiones
   */
  generateLine() {
    return '-'.repeat(PRINTER_CONFIG.width);
  }

  /**
   * Formatea moneda
   * @param {number} value - Valor a formatear
   * @returns {string} Moneda formateada
   */
  formatCurrency(value) {
    if (isNaN(value) || value === null || value === undefined) {
      return '$ 0,00';
    }
    const formatted = new Intl.NumberFormat('es-AR', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
    return `$ ${formatted}`;
  }

  /**
   * Formatea moneda sin símbolo
   * @param {number} value - Valor a formatear
   * @returns {string} Moneda formateada sin símbolo
   */
  formatCurrencyWithoutSymbol(value) {
    if (isNaN(value) || value === null || value === undefined) {
      return '0,00';
    }
    return new Intl.NumberFormat('es-AR', { 
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value);
  }

  /**
   * Alinea texto a la derecha
   * @param {string} str - Texto a alinear
   * @param {number} width - Ancho total
   * @returns {string} Texto alineado
   */
  padLeft(str, width) {
    return str.length >= width ? str.substring(0, width) : ' '.repeat(width - str.length) + str;
  }

  /**
   * Alinea texto a la izquierda
   * @param {string} str - Texto a alinear
   * @param {number} width - Ancho total
   * @returns {string} Texto alineado
   */
  padRight(str, width) {
    return str.length >= width ? str.substring(0, width) : str + ' '.repeat(width - str.length);
  }

  /**
   * Formatea el encabezado de items
   * @returns {string} Encabezado formateado
   */
  formatItemHeader() {
    const colCant = 4;
    const colDesc = 17;
    const colUnit = 10;
    const colTotal = 11;

    return `${this.padRight('Cant', colCant)} ${this.padRight('Descripcion', colDesc)} ${this.padLeft('P.Unit', colUnit)}${this.padLeft('Total', colTotal)}`;
  }

  /**
   * Formatea una línea de item
   * @param {Object} item - Datos del item
   * @returns {string} Línea formateada
   */
  formatItemLine({ cantidad, descripcion, precioUnitario, total }) {
    const colCant = 4;
    const colDesc = 17;
    const colUnit = 10;
    const colTotal = 11;

    const cantStr = String(cantidad || '0');
    const descStr = this.cleanText(descripcion || 'Sin descripción');
    const unitStr = this.formatCurrencyWithoutSymbol(precioUnitario || 0);
    const totalStr = this.formatCurrencyWithoutSymbol(total || 0);

    const cantF = this.padLeft(cantStr, colCant);
    const descF = this.padRight(descStr, colDesc);
    const unitF = this.padLeft(unitStr, colUnit);
    const totalF = this.padLeft(totalStr, colTotal);

    return `${cantF} ${descF} ${unitF}${totalF}`;
  }

  /**
   * Verifica si Web Serial API está disponible
   * @returns {boolean} true si está disponible
   */
  static isSupported() {
    return 'serial' in navigator;
  }
}

export default ThermalPrinter;
